import React, { useEffect, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SystemIntegrityBadge — Phase 4 truth surface.
 *
 * Polls `GET /api/accounts/system-integrity` every 60s and renders a
 * single badge: ✓ VALID (green) / ⚠ PARTIAL (amber) / ✗ INVALID (red).
 *
 * Click → popover with the per-check breakdown so the operator can see
 * exactly which reconciliation is drifting and by how much.
 *
 * This is THE trust gate for the entire app: one badge, one truth.
 * If this badge is red, the operator knows Accounts is disagreeing with
 * itself and should not yet be trusted to file a return.
 */
export default function SystemIntegrityBadge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIntegrity = async () => {
    try {
      const token =
        sessionStorage.getItem('token') ||
        sessionStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        localStorage.getItem('access_token');
      if (!token) {
        setError('not_authenticated');
        setLoading(false);
        return;
      }
      const res = await fetch(`${BACKEND_URL}/api/accounts/system-integrity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setError(`http_${res.status}`);
        setLoading(false);
        return;
      }
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (e) {
      setError(e.message || 'fetch_failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrity();
    const id = setInterval(fetchIntegrity, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <div
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/40 text-muted-foreground"
        data-testid="system-integrity-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span className="text-xs font-medium">Integrity</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/40 text-muted-foreground"
        title={`Integrity probe error: ${error || 'no_data'}`}
        data-testid="system-integrity-error"
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">Integrity ?</span>
      </div>
    );
  }

  const state = data.state || 'PARTIAL';
  const summary = data.summary || { pass: 0, fail: 0, partial: 0, total: 0 };

  const Visual = {
    VALID: { icon: CheckCircle2, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', label: '✓' },
    PARTIAL: { icon: AlertTriangle, color: 'text-amber-400 bg-amber-500/10 border-amber-500/30', label: '⚠' },
    INVALID: { icon: XCircle, color: 'text-rose-400 bg-rose-500/10 border-rose-500/30', label: '✗' },
    INCOMPLETE: { icon: XCircle, color: 'text-rose-400 bg-rose-500/10 border-rose-500/30', label: 'incomplete' },
    MISMATCH: { icon: XCircle, color: 'text-rose-400 bg-rose-500/10 border-rose-500/30', label: 'mismatch' },
    STALE: { icon: AlertTriangle, color: 'text-amber-400 bg-amber-500/10 border-amber-500/30', label: 'stale' },
    UNAVAILABLE: { icon: AlertTriangle, color: 'text-zinc-300 bg-zinc-500/10 border-zinc-500/30', label: 'unverified' },
  }[state] || { icon: AlertTriangle, color: 'text-amber-400 bg-amber-500/10 border-amber-500/30', label: state };
  const comp = data.completeness;

  const Icon = Visual.icon;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold transition-all hover:opacity-90',
            Visual.color
          )}
          data-testid={`system-integrity-${state.toLowerCase()}`}
          title={`Financial Integrity: ${state} (${summary.pass}/${summary.total} checks pass)`}
        >
          <Icon className="w-3.5 h-3.5" />
          <span>Integrity {Visual.label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="end" data-testid="system-integrity-popover">
        <div className={cn('px-4 py-3 border-b', Visual.color)}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">Financial Integrity: {state}</div>
              <div className="text-xs opacity-80 mt-0.5">
                {summary.pass} pass · {summary.fail} fail · {summary.partial} partial
                {data.arithmetic_state && <> · arithmetic {data.arithmetic_state}</>}
              </div>
            </div>
            <div className="text-xs opacity-70 text-right">
              {data.window?.date_from} → {data.window?.date_to}
              <br />
              {data.window?.days} day{data.window?.days === 1 ? '' : 's'}
            </div>
          </div>
        </div>

        {comp && (
          <div className="px-4 py-3 border-b border-border/40 text-xs" data-testid="integrity-completeness">
            <div className="flex items-center justify-between mb-1">
              <code className="font-mono font-semibold">sales_source_completeness</code>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted" data-testid="integrity-completeness-status">{comp.status}</span>
            </div>
            {comp.comparison_performed ? (
              <div className="text-muted-foreground">
                Sales Capture {comp.source?.count} txns · Accounts {comp.projection?.count} txns · Δ {comp.difference?.count}
                {comp.problem_days?.length > 0 && <> · {comp.problem_days.length} day(s) differ</>}
              </div>
            ) : (
              <div className="text-muted-foreground">Comparison not performed: {comp.reason}</div>
            )}
            <div className="text-[10px] text-muted-foreground mt-1">
              Last successful sync: {comp.last_successful_sync ? new Date(comp.last_successful_sync).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'never'} · repair from Sales Capture → Compare
            </div>
          </div>
        )}
        <div className="max-h-[420px] overflow-y-auto">
          {(data.checks || []).map((c, idx) => {
            const ok = c.status === 'PASS';
            const fail = c.status === 'FAIL';
            const accent = ok
              ? 'border-l-emerald-500/60'
              : fail
              ? 'border-l-rose-500/60'
              : 'border-l-amber-500/60';
            return (
              <div
                key={c.check}
                className={cn(
                  'px-4 py-3 border-l-2 border-b border-border/40 last:border-b-0',
                  accent
                )}
                data-testid={`integrity-check-${c.check}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <code className="text-xs font-mono font-semibold">{c.check}</code>
                  <span
                    className={cn(
                      'text-[10px] font-bold px-1.5 py-0.5 rounded',
                      ok && 'bg-emerald-500/20 text-emerald-400',
                      fail && 'bg-rose-500/20 text-rose-400',
                      c.status === 'PARTIAL' && 'bg-amber-500/20 text-amber-400'
                    )}
                  >
                    {c.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground leading-snug">
                  {c.reason_human}
                </div>
                {c.delta !== null && c.delta !== undefined && (
                  <div className="mt-1.5 text-[11px] flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {c.lhs?.label}: <span className="font-mono">₹{(c.lhs?.value ?? 0).toLocaleString('en-IN')}</span>
                    </span>
                    <span className="text-muted-foreground">
                      delta: <span className={cn('font-mono font-semibold', fail ? 'text-rose-400' : 'text-emerald-400')}>₹{c.delta.toLocaleString('en-IN')}</span>
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t border-border/40 text-[10px] text-muted-foreground bg-muted/20">
          Phase 4 — Global Financial Consistency Layer · refreshed every 60s
        </div>
      </PopoverContent>
    </Popover>
  );
}
